import { invoke } from '@tauri-apps/api/core';
import { isVaultLockedError, withVaultUnlocked } from '../../services/vaultGate';

/**
 * `secureStorage` capability — the only path an extension has to persist
 * secrets. Values are written to the Apiboo Vault via Rust (see
 * src-tauri/src/secure_storage.rs) as hidden items, never to
 * localStorage/app-data JSON, and no longer to the OS keychain (owner,
 * 26.10.2026: no keychain prompts). The API is unchanged: get/set/delete by
 * key, isolated per account and per extension (the host stamps the id).
 *
 * The Vault has to be unlocked. A SAVE or DELETE (something the user just
 * did) on a locked or not-yet-created Vault opens the Vault gate and goes
 * through after the unlock. A READ never opens it — an extension may read on
 * start or on a timer — it rejects with `vault_locked` /
 * `vault_needs_bootstrap`, which the extension shows as its own locked state.
 * Once the user closes the gate for an extension, that extension does not get
 * to open it again this session.
 */
const dismissedBy = new Set();

async function call(command, args, { gate }) {
  if (!gate || dismissedBy.has(args.extensionId)) return invoke(command, args);
  try {
    return await withVaultUnlocked(() => invoke(command, args), 'secure-storage');
  } catch (e) {
    if (isVaultLockedError(e)) dismissedBy.add(args.extensionId);
    throw e;
  }
}

/** Tests only. */
export function __resetSecureStorageGate() {
  dismissedBy.clear();
}

export const secureStorageCapability = {
  capability: 'secureStorage',
  methods: {
    'secureStorage.set': (extensionId, params) =>
      call(
        'secure_storage_set',
        {
          extensionId,
          key: params.key,
          value: params.value,
        },
        { gate: true }
      ),
    'secureStorage.get': (extensionId, params) =>
      call('secure_storage_get', { extensionId, key: params.key }, { gate: false }),
    'secureStorage.delete': (extensionId, params) =>
      call('secure_storage_delete', { extensionId, key: params.key }, { gate: true }),
  },
};
